import { algoliasearch } from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import { createWidgetInstances } from '@js/algolia/createWidgetInstance.js';
import { setTexts, texts } from '@js/algolia/common.js';
import { connectHits } from 'instantsearch.js/es/connectors';
import {
  createPagination,
  createPaginationVisibility,
} from '@js/algolia/createPagination.js';
import { createCurrentRefinements } from '@js/algolia/currentRefinements.js';
import { createPopularCategories } from '@js/algolia/createPopularCategories.js';
import {
  createStateMapping,
  historyRouter,
} from '@js/algolia/historyRouter.js';

export function createSearchGrid({
  appId,
  apiKey,
  indexName,
  configureWidget = null,
  formatCardData,
  scrollToSelector = '[data-search-videos]',
}) {
  return ($el) => ({
    results: [],
    appId,
    apiKey,
    indexName,
    isLoaded: false,
    isSearchComplete: false,
    noResults: false,
    totalHits: 0,
    el: $el,
    search: null,
    activeRefinements: {},
    hasError: false,
    errorMessage: '',

    init() {
      setTexts(this.el);
      if (!this.appId || !this.apiKey || !this.indexName) {
        this.handleError(texts.errorConfig);
        return;
      }
      this.setupSearch();
    },

    handleError(message, error = null) {
      this.hasError = true;
      this.isLoaded = true;
      this.errorMessage = message;

      if (error) {
        console.error('Algolia search error:', error);
      }

      window.dispatchEvent(
        new CustomEvent('algolia:error', {
          detail: { message, error, element: this.el },
        }),
      );
    },

    getInitialToggleState() {
      const toggleState = {};
      const toggleWidgets = document.querySelectorAll(
        '[data-widget-type="toggleRefinement"][data-checked="true"]',
      );
      toggleWidgets.forEach((widget) => {
        const attribute = widget.dataset.attribute;
        if (attribute) {
          toggleState[attribute] = true;
        }
      });
      return toggleState;
    },

    getInitialRefinementState() {
      const refinementState = {};
      const widgets = document.querySelectorAll(
        '[data-widget-auto][data-default-refinement]',
      );
      widgets.forEach((widget) => {
        const attribute = widget.dataset.attribute;
        const defaultValue = widget.dataset.defaultRefinement;
        if (attribute && defaultValue) {
          refinementState[attribute] = defaultValue
            .split(',')
            .map((v) => v.trim());
        }
      });
      return refinementState;
    },

    async setupSearch() {
      try {
        const searchClient = this.createSearchClient();
        const toggleState = this.getInitialToggleState();
        const refinementState = this.getInitialRefinementState();

        this.search = instantsearch({
          indexName: this.indexName,
          searchClient,
          future: {
            preserveSharedStateOnUnmount: true,
            cleanUrlOnDispose: false,
          },
          initialUiState: {
            [this.indexName]: {
              toggle: toggleState,
              refinementList: refinementState,
            },
          },
          routing: {
            router: historyRouter(),
            stateMapping: createStateMapping(
              this.indexName,
              toggleState,
              refinementState,
            ),
          },
        });

        const widgetContainers = Array.from(
          document.querySelectorAll('[data-widget-auto]'),
        );
        const widgetInstances = createWidgetInstances(widgetContainers);

        const structuredResults = this.generateStructuredResults();

        const paginationWidget = createPagination(
          '[data-pagination]',
          scrollToSelector,
        );
        const paginationVisibility =
          createPaginationVisibility('[data-pagination]');

        const refinementsWidget = createCurrentRefinements();
        const popularCategoriesWidget = createPopularCategories();

        const widgets = [];

        if (configureWidget) {
          widgets.push(configureWidget);
        }

        widgets.push(...widgetInstances);
        widgets.push(structuredResults());

        if (paginationWidget) {
          widgets.push(paginationWidget);
        }

        if (paginationVisibility) {
          widgets.push(paginationVisibility);
        }

        if (refinementsWidget) {
          widgets.push(refinementsWidget);
        }

        if (popularCategoriesWidget) {
          widgets.push(popularCategoriesWidget);
        }

        this.search.addWidgets(widgets.filter(Boolean));

        this.search.on('error', ({ error }) => this.onSearchError(error));
        this.search.on('render', () => this.onSearchRender());

        await this.search.start();

        this.watchRefinements();
      } catch (error) {
        this.handleError(texts.errorUnavailable, error);
      }
    },

    // Query excluded — scrolling mid-keystroke fights typing.
    watchRefinements() {
      const helper = this.search?.helper;
      if (!helper) return;

      const signature = (state) =>
        JSON.stringify([
          state.disjunctiveFacetsRefinements,
          state.facetsRefinements,
          state.hierarchicalFacetsRefinements,
          state.numericRefinements,
        ]);

      let previous = signature(helper.state);

      helper.on('change', ({ state }) => {
        const current = signature(state);
        if (current === previous) return;
        previous = current;
        document.querySelector(scrollToSelector)?.scrollIntoView();
      });
    },

    createSearchClient() {
      const client = algoliasearch(this.appId, this.apiKey);
      const self = this;

      return {
        ...client,
        async search(requests) {
          try {
            return await client.search(requests);
          } catch (error) {
            self.onSearchError(error);
            throw error;
          }
        },
      };
    },

    onSearchError(error) {
      let message = texts.errorUnavailable;

      if (error.status === 429) {
        message = texts.errorRateLimit;
      } else if (error.status === 403 || error.status === 401) {
        message = texts.errorAuth;
      } else if (
        error.message?.includes('network') ||
        error.name === 'TypeError'
      ) {
        message = texts.errorNetwork;
      }

      this.handleError(message, error);
    },

    generateStructuredResults() {
      return connectHits(({ hits, results }) => {
        if (results?.nbHits !== undefined) {
          this.isSearchComplete = true;
          this.noResults = hits.length === 0;
        }
        this.results = hits;
        this.totalHits = results?.nbHits || 0;
      });
    },

    onSearchRender() {
      this.isLoaded = true;
      this.updateActiveRefinements();
    },

    updateActiveRefinements() {
      if (!this.search) return;
      const uiState = this.search.getUiState();
      const indexState = uiState[this.indexName] || {};
      this.activeRefinements = indexState.refinementList || {};
    },

    hasMultipleRefinements(attribute) {
      const values = this.activeRefinements[attribute];
      return values && values.length >= 2;
    },

    hasNoRefinements(attribute) {
      const values = this.activeRefinements[attribute];
      return !values || values.length === 0;
    },

    formatArray(arr, separator = ', ') {
      if (!arr || !Array.isArray(arr)) return '';
      return arr.join(separator);
    },

    formatCardData,
  });
}
